import { FC, MouseEvent, ReactNode, useState } from 'react'
import { displayFilename, isThumbnailContentType } from '../helpers/chatAttachments'

import Box from '@mui/material/Box'
import { ConversationAttachmentDTO } from 'models/conversations'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { formatBytes } from 'utils/formatting/formatBytes'

interface Props {
  attachments: ConversationAttachmentDTO[]
  isOutgoing: boolean
  onImageError?: (attachment: ConversationAttachmentDTO) => void
  onImageLoad?: () => void
  onOpenFile?: (attachment: ConversationAttachmentDTO, event: MouseEvent<HTMLElement>) => void
}

const linkSx = { color: 'inherit', textDecoration: 'none', display: 'block', maxWidth: '100%' }

export const MessageAttachments: FC<Props> = ({ attachments, isOutgoing, onImageError, onImageLoad, onOpenFile }) => {
  const [brokenUrls, setBrokenUrls] = useState<ReadonlySet<string>>(new Set())
  const rowBackground = isOutgoing ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.04)'
  const secondaryColor = isOutgoing ? 'rgba(255,255,255,0.7)' : 'inherit'

  const handleImageError = (attachment: ConversationAttachmentDTO, url: string) => {
    setBrokenUrls((previous) => new Set(previous).add(url))
    onImageError?.(attachment)
  }

  const renderLink = (attachment: ConversationAttachmentDTO, content: ReactNode, download: boolean) => (
    <Box
      component="a"
      href={attachment.url ?? undefined}
      target="_blank"
      rel="noopener noreferrer"
      download={download ? displayFilename(attachment.filename) : undefined}
      onClick={(event: MouseEvent<HTMLElement>) => onOpenFile?.(attachment, event)}
      sx={linkSx}
    >
      {content}
    </Box>
  )

  return (
    <Stack gap="0.6rem" data-testid="chat-message-attachments" sx={{ maxWidth: '100%' }}>
      {attachments.map((attachment) => {
        const filename = displayFilename(attachment.filename)
        const url = attachment.url
        const showThumbnail = !!url && isThumbnailContentType(attachment.contentType) && !brokenUrls.has(url)

        if (showThumbnail) {
          return (
            <Box key={attachment.id} data-testid="chat-message-attachment-image">
              {renderLink(
                attachment,
                <Box
                  component="img"
                  src={url}
                  alt={filename}
                  loading="lazy"
                  onLoad={() => onImageLoad?.()}
                  onError={() => handleImageError(attachment, url)}
                  sx={{
                    display: 'block',
                    maxWidth: '100%',
                    maxHeight: '24rem',
                    borderRadius: '0.8rem',
                    objectFit: 'cover',
                  }}
                />,
                false
              )}
            </Box>
          )
        }

        const row = (
          <Stack
            direction="row"
            alignItems="center"
            gap="0.8rem"
            sx={{ padding: '0.8rem 1rem', borderRadius: '0.8rem', backgroundColor: rowBackground, minWidth: 0 }}
          >

            <InsertDriveFileOutlinedIcon sx={{ fontSize: '2.2rem', flexShrink: 0 }} />

            <Stack minWidth={0}>

              <Typography variant="text-sm" noWrap title={filename} sx={{ color: 'inherit', fontWeight: 600 }}>
                {filename}
              </Typography>

              <Typography variant="text-xs" sx={{ color: secondaryColor }}>
                {formatBytes(attachment.sizeBytes, 1)}
              </Typography>

            </Stack>

          </Stack>
        )

        return (
          <Box key={attachment.id} data-testid="chat-message-attachment-file">
            {url ? renderLink(attachment, row, true) : row}
          </Box>
        )
      })}
    </Stack>
  )
}
